import React, { useState, useEffect } from "react";
import "./Staff.css";
import * as ApiService from "../services/ApiService";
import MassWeightModal from "./MassWeightModal";
import { useAuth0 } from "@auth0/auth0-react";
import { BsLock } from "react-icons/bs";
import PremiumRibbon from "./PremiumRibbon";
import Tooltip from "./Tooltip";
import WarningModal from "./WarningModal";

const Staff = () => {
  const { user, getAccessTokenSilently } = useAuth0();
  const isPremiumUser =
    user &&
    user[`https://calllistscheduler.netlify.app/roles`]?.includes("premium");
  const [doctors, setDoctors] = useState([]);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [originalEditingDoctor, setOriginalEditingDoctor] = useState(null);
  const [showMassWeightModal, setShowMassWeightModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingWeightChange, setPendingWeightChange] = useState(null);
  const [newDoctor, setNewDoctor] = useState({
    abbreviation: "",
    surname: "",
    givenName: "",
    active: "Yes",
    senior: false,
    schedulingWeight: 1.0,
  });

  const isDevelopment = process.env.NODE_ENV === "development";

  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      const data = await ApiService.fetchDoctors(getAccessTokenSilently);
      setDoctors(data);
    } catch (error) {
      console.error("Error fetching doctors:", error);
    }
  };

  const handleAddDoctor = async (e) => {
    e.preventDefault();
    try {
      const response = await ApiService.addDoctor(
        getAccessTokenSilently,
        newDoctor
      );
      if (response.ok) {
        setNewDoctor({
          abbreviation: "",
          surname: "",
          givenName: "",
          active: "Yes",
          senior: false,
          schedulingWeight: 1.0,
        });
        fetchDoctors();
      }
    } catch (error) {
      console.error("Error adding doctor:", error);
    }
  };

  const handleUpdateDoctor = async (e) => {
    e.preventDefault();
    await proceedWithSingleUpdate(editingDoctor);
  };

  const proceedWithSingleUpdate = async (doctorToUpdate) => {
    try {
      const response = await ApiService.updateDoctor(
        getAccessTokenSilently,
        doctorToUpdate.staffId,
        doctorToUpdate
      );
      if (response.ok) {
        setEditingDoctor(null);
        setOriginalEditingDoctor(null);
        fetchDoctors();
      }
    } catch (error) {
      console.error("Error updating doctor:", error);
      alert("Failed to update doctor.");
    }
  };

  const handleDeleteDoctor = async (staffId) => {
    if (window.confirm("Are you sure you want to delete this doctor?")) {
      try {
        const response = await ApiService.deleteDoctor(
          getAccessTokenSilently,
          staffId
        );
        if (response.ok) {
          fetchDoctors();
        }
      } catch (error) {
        console.error("Error deleting doctor:", error);
      }
    }
  };

  const handleMassWeightUpdate = async (selectedDoctorIds, weight) => {
    await proceedWithMassUpdate(selectedDoctorIds, weight);
  };

  const proceedWithMassUpdate = async (selectedDoctorIds, weight) => {
    try {
      await ApiService.updateMassWeight(
        getAccessTokenSilently,
        selectedDoctorIds,
        weight
      );
      setShowMassWeightModal(false);
      fetchDoctors();
    } catch (error) {
      console.error("Error updating weights:", error);
      alert("Failed to update weights: " + (error.message || "Unknown error"));
    }
  };

  const handleConfirmWarning = async () => {
    setShowWarningModal(false);
    if (!pendingWeightChange) return;

    if (pendingWeightChange.type === "single") {
      await proceedWithSingleUpdate(pendingWeightChange.data);
    } else if (pendingWeightChange.type === "mass") {
      await proceedWithMassUpdate(
        pendingWeightChange.data.selectedDoctorIds,
        pendingWeightChange.data.weight
      );
    }
    setPendingWeightChange(null);
  };

  const handleCancelWarning = () => {
    setShowWarningModal(false);
    if (pendingWeightChange?.type === "single") {
      setEditingDoctor(originalEditingDoctor);
    }
    setPendingWeightChange(null);
  };

  const formatSchedulingWeight = (weight) => {
    const str = weight.toString();
    if (!str.includes(".")) {
      return str + ".0";
    }
    return str;
  };

  const schedulingWeightTooltipText = `
  Scheduling weight determines the relative frequency of on-call assignments:<br>
  • 1.0 = Full load (100% of normal on-call duties)<br>
  • 0.7 = Reduced load (70% of normal on-call duties)<br>
  • 0.5 = Half load (50% of normal on-call duties)
  `;

  return (
    <div className="page-container">
      <div className="staff-header">
        <h2>Staff Management</h2>
        <div className="header-actions">
          <button
            className="mass-weight-button"
            onClick={() => setShowMassWeightModal(true)}
          >
            Update Multiple Weights
          </button>
        </div>
      </div>

      <form
        onSubmit={handleAddDoctor}
        className={`doctor-form ${!isPremiumUser ? "premium-locked" : ""}`}
      >
        <h3>
          Add New Doctor
          {!isPremiumUser && (
            <>
              <BsLock className="lock-icon" />
              <PremiumRibbon />
            </>
          )}
        </h3>
        {!isPremiumUser ? (
          <Tooltip>
            <div className="form-grid">
              <FormContent
                newDoctor={newDoctor}
                setNewDoctor={setNewDoctor}
                isPremiumUser={isPremiumUser}
                schedulingWeightTooltipText={schedulingWeightTooltipText}
              />
            </div>
          </Tooltip>
        ) : (
          <div className="form-grid">
            <FormContent
              newDoctor={newDoctor}
              setNewDoctor={setNewDoctor}
              isPremiumUser={isPremiumUser}
              schedulingWeightTooltipText={schedulingWeightTooltipText}
            />
          </div>
        )}
      </form>

      <div className="table-container">
        {doctors.length === 0 ? (
          <div className="table-no-data">
            No doctors have been added yet. Create one using the form above.
          </div>
        ) : (
          <table className="staff-table">
            <thead>
              <tr>
                <th>Abbreviation</th>
                <th>Surname</th>
                <th>Given Name</th>
                <th>Active</th>
                <th>Senior</th>
                <th>
                  <span
                    className="scheduling-weight-header"
                    onMouseEnter={(e) => {
                      const tooltip = document.createElement("div");
                      tooltip.className = "weight-tooltip";
                      tooltip.innerHTML = schedulingWeightTooltipText;
                      document.body.appendChild(tooltip);
                      const rect = e.target.getBoundingClientRect();
                      tooltip.style.left = `${rect.left + window.scrollX}px`;
                      tooltip.style.top = `${
                        rect.bottom + window.scrollY + 5
                      }px`;
                    }}
                    onMouseLeave={() => {
                      const tooltip = document.querySelector(".weight-tooltip");
                      if (tooltip) tooltip.remove();
                    }}
                  >
                    Scheduling Weight
                  </span>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {doctors.map((doctor) => (
                <tr
                  key={doctor.staffId}
                  className={
                    editingDoctor?.staffId === doctor.staffId ? "editing" : ""
                  }
                >
                  {editingDoctor?.staffId === doctor.staffId ? (
                    <>
                      <td>
                        <input
                          type="text"
                          value={editingDoctor.abbreviation}
                          onChange={(e) =>
                            setEditingDoctor({
                              ...editingDoctor,
                              abbreviation: e.target.value,
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={editingDoctor.surname}
                          onChange={(e) =>
                            setEditingDoctor({
                              ...editingDoctor,
                              surname: e.target.value,
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={editingDoctor.givenName}
                          onChange={(e) =>
                            setEditingDoctor({
                              ...editingDoctor,
                              givenName: e.target.value,
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={editingDoctor.active === "Yes"}
                          onChange={(e) =>
                            setEditingDoctor({
                              ...editingDoctor,
                              active: e.target.checked ? "Yes" : "No",
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={editingDoctor.senior}
                          onChange={(e) =>
                            setEditingDoctor({
                              ...editingDoctor,
                              senior: e.target.checked,
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="1"
                          value={editingDoctor.schedulingWeight}
                          onChange={(e) =>
                            setEditingDoctor({
                              ...editingDoctor,
                              schedulingWeight: parseFloat(e.target.value),
                            })
                          }
                        />
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button onClick={handleUpdateDoctor}>Save</button>
                          <button
                            onClick={() => {
                              setEditingDoctor(null);
                              setOriginalEditingDoctor(null);
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{doctor.abbreviation}</td>
                      <td>{doctor.surname}</td>
                      <td>{doctor.givenName}</td>
                      <td>{doctor.active === "Yes" ? "Yes" : "No"}</td>
                      <td>{doctor.senior ? "Yes" : "No"}</td>
                      <td>{formatSchedulingWeight(doctor.schedulingWeight)}</td>
                      <td>
                        <button
                          onClick={() => {
                            setEditingDoctor({ ...doctor });
                            setOriginalEditingDoctor({ ...doctor });
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteDoctor(doctor.staffId)}
                        >
                          Delete
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showMassWeightModal && (
        <MassWeightModal
          doctors={doctors}
          onClose={() => setShowMassWeightModal(false)}
          onSave={handleMassWeightUpdate}
        />
      )}

      {showWarningModal && (
        <WarningModal
          onConfirm={handleConfirmWarning}
          onCancel={handleCancelWarning}
        />
      )}
    </div>
  );
};

const FormContent = ({
  newDoctor,
  setNewDoctor,
  isPremiumUser,
  schedulingWeightTooltipText,
}) => (
  <>
    <div className="form-row top-row">
      <div className="input-group">
        <input
          type="text"
          placeholder="Abbreviation"
          value={newDoctor.abbreviation}
          onChange={(e) =>
            setNewDoctor({ ...newDoctor, abbreviation: e.target.value })
          }
          required
          disabled={!isPremiumUser}
        />
      </div>
      <div className="input-group">
        <input
          type="text"
          placeholder="Surname"
          value={newDoctor.surname}
          onChange={(e) =>
            setNewDoctor({ ...newDoctor, surname: e.target.value })
          }
          required
          disabled={!isPremiumUser}
        />
      </div>
      <div className="input-group">
        <input
          type="text"
          placeholder="Given Name"
          value={newDoctor.givenName}
          onChange={(e) =>
            setNewDoctor({ ...newDoctor, givenName: e.target.value })
          }
          required
          disabled={!isPremiumUser}
        />
      </div>
    </div>

    <div className="form-row">
      <div className="input-group horizontal">
        <label>Scheduling Weight</label>
        <input
          type="number"
          step="0.1"
          min="0"
          max="1"
          value={newDoctor.schedulingWeight}
          onChange={(e) =>
            setNewDoctor({
              ...newDoctor,
              schedulingWeight: parseFloat(e.target.value),
            })
          }
          required
          disabled={!isPremiumUser}
          onMouseEnter={(e) => {
            const tooltip = document.createElement("div");
            tooltip.className = "weight-tooltip";
            tooltip.innerHTML = schedulingWeightTooltipText;
            document.body.appendChild(tooltip);
            const rect = e.target.getBoundingClientRect();
            tooltip.style.left = `${rect.left + window.scrollX}px`;
            tooltip.style.top = `${rect.bottom + window.scrollY + 5}px`;
          }}
          onMouseLeave={() => {
            const tooltip = document.querySelector(".weight-tooltip");
            if (tooltip) tooltip.remove();
          }}
        />
      </div>

      <div className="checkbox-group">
        <label>
          <input
            type="checkbox"
            checked={newDoctor.senior}
            onChange={(e) =>
              setNewDoctor({ ...newDoctor, senior: e.target.checked })
            }
            disabled={!isPremiumUser}
          />
          <span>Senior Doctor</span>
        </label>
      </div>

      <button
        type="submit"
        className="add-doctor-btn"
        disabled={!isPremiumUser}
      >
        Add Doctor
      </button>
    </div>
  </>
);

export default Staff;
